import React, {
  Fragment, ReactElement, ReactNode, useState,
} from 'react'
import styled from 'styled-components'
import { lighten } from 'polished'
import theme from '../lib/theme'
import colors from '../lib/colors'
import Loading from '../components/Loading'
import Modal from '../components/Modal'

// ACTION BUTTONS: Save / Manual Order / Create/Add New / Redeem / Publish / Search/Check Validity
// PRIMARY BUTTONS: Edit / Download/Export / Upload / Select/Go / Drag / View
// SECONDARY BUTTONS: Apply Filters / Check Validity / Category type / Gift item type / unpublish
// DANGER BUTTONS: Remove / Delete / Cancel

export enum ButtonStyle {
  Action = 'action',
  Primary = 'primary',
  Secondary = 'secondary',
  Danger = 'danger',
}

export enum ButtonSize {
  Large = 'large',
  Standard = 'standard',
  Mini = 'mini',
  Table = 'table',
}

const StyledButton = styled.button`
  position: relative;
  padding: 0 10px;
  min-width: 40px;
  min-height: ${theme.fieldHeight};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  transition: 0.3s;
  font-weight: bold;
  white-space: nowrap;

  &.button-large {
    min-height: ${theme.largeFieldHeight};
    padding: 0 14px;
    font-size: 16px;
    width: fit-content;
  }

  &.button-standard {
    width: fit-content;
  }

  &.button-mini {
    font-size: 0.9rem;
    padding: 0 8px;
    min-width: 0;
    min-height: 22px;
  }

  &.button-table {
    font-size: 0.8rem;
    padding: 0 5px;
    min-width: 0;
    min-height: 18px;
  }

  .button-loading-hidden {
    height: 0;
    opacity: 0;
    display: inherit;
  }

  &.action {
    background-color: ${colors.green};
    color: ${colors.white};
    :hover {
      background-color: ${colors.lightGreen};
    }
    :active {
      background-color: ${colors.green};
    }
  }
  &.primary {
    background-color: ${colors.blue};
    color: ${colors.white};
    :hover {
      background-color: ${colors.lightBlue};
    }
    :active {
      background-color: ${colors.blue};
    }
  }
  &.secondary {
    background-color: ${colors.gray};
    color: ${colors.black};
    :hover {
      background-color: ${colors.darkGray};
    }
    :active {
      background-color: ${colors.darkerGray};
    }
  }
  &.danger {
    background-color: ${colors.red};
    color: ${colors.white};
    :hover {
      background-color: ${colors.lightRed};
    }
    :active {
      background-color: ${colors.red};
    }
  }
  &.custom {
    background-color: ${(props): string => props.theme[0]};
    color: ${(props): string => props.theme[1]};
    :hover {
      background-color: ${(props): string => props.theme[2]};
    }
    :active {
      background-color: ${(props): string => props.theme[0]};
    }
  }
  :active {
    transition: 0s;
  }
  :disabled {
    cursor: not-allowed;
    background-color: ${colors.darkGray};
    color: ${colors.darkerGray};
    :hover {
      background-color: ${colors.darkGray};
      color: ${colors.darkerGray};
    }
  }
  &.loading {
    pointer-events: none;
  }
`
const ModalContents = styled.div`
  p {
    margin: 10px auto 0;
  }
  min-width: 200px;
  max-width: 360px;
  text-align: center;
  font-weight: bold;
`
const ButtonWrapper = styled.div`
  padding-top: 10px;
  button {
    margin: 10px;
    padding: 5px;
  }
`
const LoadingContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

export interface IButtonProps {
  onClick?: (() => void) | (() => Promise<void>)
  buttonStyle?: ButtonStyle
  disabled?: boolean
  // linting bug - `loading` is used...
  // eslint-disable-next-line react/no-unused-prop-types
  loading?: boolean
  showConfirmation?: boolean
  confirmationMessage?: string
  confirmationStyle?: ButtonStyle
  onConfirmationCancel?: () => void
  buttonSize?: ButtonSize
  children?: ReactNode
  type?: 'button' | 'submit' | 'reset'
  style?: object
  className?: string
  customColors?: [string, string]
}

function Button(props: IButtonProps): ReactElement {
  const {
    disabled,
    onClick,
    children,
    buttonStyle = ButtonStyle.Primary,
    loading,
    showConfirmation,
    confirmationMessage,
    confirmationStyle = ButtonStyle.Danger,
    onConfirmationCancel,
    buttonSize,
    type,
    style,
    className,
    customColors,
  } = props

  const [confirmationShowing, setConfirmationShowing] = useState<boolean>(false)

  const handleClick = (): void => {
    if (onClick) {
      if (showConfirmation) {
        setConfirmationShowing(true)
      } else {
        onClick()
      }
    }
  }

  const onConfirmationClick = (): void => {
    setConfirmationShowing(false)
    if (onClick) {
      onClick()
    }
  }

  const { blue, lightBlue, white } = colors
  const customBackColor = customColors && customColors.length === 2 ? customColors[0] : blue
  const customTextColor = customColors && customColors.length === 2 ? customColors[1] : white
  const customHighlightColor = customColors && customColors.length === 2
    ? lighten(0.1, customColors[0])
    : lightBlue

  const loadingClass = loading ? 'loading' : ''
  const sizeClass = `button-${buttonSize}`
  const customClass = customColors && customColors.length === 2 ? 'custom' : ''

  return (
    <Fragment>
      <StyledButton
        onClick={handleClick}
        disabled={disabled}
        className={`${className} ${buttonStyle} ${loadingClass} ${sizeClass} ${customClass}`}
        type={type}
        style={style}
        theme={[customBackColor, customTextColor, customHighlightColor]}
      >
        {loading && (
          <LoadingContainer>
            <Loading
              style={{
                transform: buttonSize === ButtonSize.Table ? 'scale(0.8)' : '',
              }}
            />
          </LoadingContainer>
        )}
        <div className={loading ? 'button-loading-hidden' : ''}>{children}</div>
      </StyledButton>
      {confirmationShowing && (
        <Modal
          modalOpen={confirmationShowing}
          onModalClose={(): void => setConfirmationShowing(false)}
          modalAction={onConfirmationClick}
        >
          <ModalContents>
            <p>{confirmationMessage || 'Are you sure?'}</p>
            <ButtonWrapper>
              <StyledButton
                className={confirmationStyle}
                type="button"
                onClick={onConfirmationClick}
              >
                Yes
              </StyledButton>
              <StyledButton
                className={ButtonStyle.Secondary}
                type="button"
                onClick={(): void => {
                  if (onConfirmationCancel) onConfirmationCancel()
                  setConfirmationShowing(false)
                }}
              >
                No
              </StyledButton>
            </ButtonWrapper>
          </ModalContents>
        </Modal>
      )}
    </Fragment>
  )
}
export default Button

// AsyncButton is just a regular Button with the loading state set when we call handleClick
export function AsyncButton(props: IButtonProps): ReactElement {
  const {
    disabled,
    onClick,
    buttonStyle,
    children,
    showConfirmation,
    confirmationMessage,
    confirmationStyle,
    onConfirmationCancel,
    buttonSize,
    type,
    style,
    className,
    customColors,
  } = props

  const [loading, setLoading] = useState(false)

  return (
    <Button
      onClick={async (): Promise<void> => {
        setLoading(true)
        if (onClick) {
          await onClick()
        }
        setLoading(false)
      }}
      className={className}
      buttonStyle={buttonStyle}
      disabled={disabled}
      loading={loading}
      showConfirmation={showConfirmation}
      confirmationMessage={confirmationMessage}
      confirmationStyle={confirmationStyle}
      onConfirmationCancel={onConfirmationCancel}
      buttonSize={buttonSize}
      type={type}
      style={style}
      customColors={customColors}
    >
      {children}
    </Button>
  )
}
